import { DestroyRef, Inject, Injectable, Optional, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { gql } from 'apollo-angular';
import AWSAppSyncClient, { AUTH_TYPE } from 'aws-appsync';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { from, of } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs';
import { LoggerService } from '@content-platform/web-angular-shared-util-logging';
import { evaluate, onFlagChange } from 'shared-feature-flag-admin-shared';
import { featureFlagActions } from '../actions';
import { decodeEvaluatedFlagValue } from '../utils/evaluated-flag-value-codec';
import * as i0 from "@angular/core";
import * as i1 from "@ngrx/store";
import * as i2 from "@content-platform/web-angular-shared-util-logging";
/** AppSync region (matches the shared AppSyncProvider). */
const AWS_REGION = 'us-east-1';
/** Non-secret lookup identifier for the access-token entry in the OktaAuth tokenManager. */
const OKTA_ACCESS_ENTRY_NAME = 'accessToken';
/** Reconnect backoff bounds for the onFlagChange socket (ms). */
const RECONNECT_BASE_DELAY_MS = 1_000;
const RECONNECT_MAX_DELAY_MS = 30_000;
/**
 * In-house feature-flag backend (GraphQL `evaluate` query + `onFlagChange` AppSync
 * subscription), used as a drop-in replacement for the LaunchDarkly path behind the
 * `launchDarkly.useInHouseBackend` runtime switch.
 *
 * INTERNAL to this lib — deliberately NOT re-exported from `src/index.ts`. Consumers keep
 * using {@link WebAngularSharedDataAccessFeatureFlagApisService}; this client only reuses the
 * SAME NgRx seam (SetFlags on bootstrap/identify, UpdateFlags on each push) so getFlagAsync(),
 * the directives, selectors and the readiness guard all keep working unchanged.
 */
export class InHouseFeatureFlagClient {
    constructor(store, rootLogger, oktaAuth) {
        this.store = store;
        this.oktaAuth = oktaAuth;
        this.destroyRef = inject(DestroyRef);
        /** flagKey -> last applied etag; used to drop duplicate/replayed pushes. */
        this.seenEtags = new Map();
        /** Local mirror of the values we last pushed to the store, used to fill UpdateFlags.previous. */
        this.localFlags = {};
        this.config = null;
        this.currentUser = null;
        /** Self-contained AppSync client (its own hydrated instance; not the consumer app's Apollo). */
        this.hydratedClient = null;
        this.liveSubscription = null;
        this.reconnectTimer = null;
        this.reconnectDelay = RECONNECT_BASE_DELAY_MS;
        this.stopped = false;
        this.logger = rootLogger.instance('InHouseFeatureFlagClient');
        this.destroyRef.onDestroy(() => this.stop());
    }
    /**
     * Bootstrap (Evaluate -> SetFlags) then open the live subscription.
     * Returns an observable that emits once when the first evaluation settles.
     */
    start(config, user) {
        this.stopped = false;
        this.config = config;
        this.currentUser = this.buildUserFromInfo(user);
        return from(this.bootstrap()).pipe(tap(() => this.openSubscription()), map(() => void 0), catchError((err) => {
            this.logger.error('In-house feature-flag bootstrap failed; loading app with empty flag set.', err);
            this.finishInitializationWithoutFlags();
            this.openSubscription();
            return of(void 0);
        }), take(1));
    }
    /** Re-run bootstrap for a new user — mirrors LDClient.identify() / loadFlagsForUser(). */
    identify(user) {
        if (!this.config) {
            return;
        }
        this.currentUser = this.buildUserFromInfo(user);
        from(this.bootstrap())
            .pipe(catchError((err) => {
            this.logger.error('In-house feature-flag identify failed.', err);
            return of(void 0);
        }), take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.openSubscription());
    }
    /** Tear down the live subscription and any pending reconnect. */
    stop() {
        this.stopped = true;
        this.teardownSubscription();
        this.hydratedClient = null;
    }
    async bootstrap() {
        const config = this.requireConfig();
        this.seenEtags.clear();
        const client = await this.getClient();
        const variables = {
            projectKey: config.projectKey,
            clientId: config.clientId,
            user: this.currentUser,
            flagKeys: null
        };
        const result = await client.query({
            query: gql(evaluate),
            variables: variables,
            fetchPolicy: 'network-only'
        });
        const flags = this.toFlagSet(result?.data);
        this.replaceStoreFlags(flags);
    }
    openSubscription() {
        if (this.stopped) {
            return;
        }
        this.teardownSubscription();
        const config = this.requireConfig();
        from(this.getClient())
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe({
            next: (client) => {
                const variables = {
                    projectKey: config.projectKey,
                    clientId: config.clientId
                };
                const observable = client.subscribe({
                    query: gql(onFlagChange),
                    variables: variables
                });
                this.liveSubscription = observable.subscribe({
                    next: (push) => this.handlePush(push?.data?.onFlagChange ?? null),
                    error: (err) => this.handleSubscriptionError(err)
                });
            },
            error: (err) => this.handleSubscriptionError(err)
        });
    }
    handlePush(change) {
        if (!change) {
            return;
        }
        // A push proves the socket is healthy — reset the reconnect backoff.
        this.reconnectDelay = RECONNECT_BASE_DELAY_MS;
        const { flagKey, etag, value } = change;
        if (this.seenEtags.get(flagKey) === etag) {
            return;
        }
        this.seenEtags.set(flagKey, etag);
        const parsed = this.parsePushValue(value);
        // GENERAL flag -> the pushed value is correct for everyone. TARGETED flag -> the per-user
        // result depends on rules, so re-run evaluate for just this flag. Do NOT gate on a
        // locally-known `currentUser`: consumer apps (e.g. the vcap-ui sandbox) don't always
        // wire FEATURE_FLAG_USER_INFO, but Okta-authenticated evaluate calls still resolve the
        // caller from the authorizer's `resolverContext.username` on the server. Gating the
        // re-evaluate on `this.currentUser` silently applied the env DEFAULT (parsed.value) and
        // left targeted STRING / JSON flags showing the wrong value until a refresh —
        // matching the manual repro on VCAP-1410's PR sandbox.
        if (parsed.matchedRule != null) {
            void this.reEvaluateFlag(flagKey, parsed.value);
            return;
        }
        this.applyFlagUpdate(flagKey, parsed.value);
    }
    async reEvaluateFlag(flagKey, fallbackValue) {
        const config = this.requireConfig();
        try {
            const client = await this.getClient();
            const variables = {
                projectKey: config.projectKey,
                clientId: config.clientId,
                user: this.currentUser,
                flagKeys: [flagKey]
            };
            const result = await client.query({
                query: gql(evaluate),
                variables: variables,
                fetchPolicy: 'network-only'
            });
            const flag = result?.data?.evaluate?.flags?.find((entry) => entry.flagKey === flagKey);
            const value = flag ? this.parseAwsJson(flag.value) : fallbackValue;
            this.applyFlagUpdate(flagKey, value);
        }
        catch (err) {
            this.logger.error(`Re-evaluate of targeted flag "${flagKey}" failed; applying pushed value.`, err);
            this.applyFlagUpdate(flagKey, fallbackValue);
        }
    }
    handleSubscriptionError(err) {
        this.teardownSubscription();
        if (this.stopped) {
            return;
        }
        const delay = this.reconnectDelay;
        this.reconnectDelay = Math.min(this.reconnectDelay * 2, RECONNECT_MAX_DELAY_MS);
        this.logger.error(`onFlagChange subscription error; reconnecting in ${delay}ms.`, err);
        this.reconnectTimer = setTimeout(() => {
            // Re-bootstrap on resume to catch events missed while disconnected (api-contract §4.5).
            from(this.bootstrap())
                .pipe(catchError((bootstrapErr) => {
                this.logger.error('Re-bootstrap after reconnect failed.', bootstrapErr);
                return of(void 0);
            }), take(1), takeUntilDestroyed(this.destroyRef))
                .subscribe(() => this.openSubscription());
        }, delay);
    }
    applyFlagUpdate(flagKey, value) {
        const previous = this.localFlags[flagKey];
        this.localFlags[flagKey] = value;
        this.store.dispatch(new featureFlagActions.UpdateFlags({
            changes: { [flagKey]: { current: value, previous } },
            project: this.requireConfig().projectKey
        }));
    }
    replaceStoreFlags(flags) {
        Object.keys(this.localFlags).forEach((key) => delete this.localFlags[key]);
        Object.assign(this.localFlags, flags);
        this.store.dispatch(new featureFlagActions.SetFlags({ flags, project: this.requireConfig().projectKey }));
    }
    finishInitializationWithoutFlags() {
        this.replaceStoreFlags({});
    }
    toFlagSet(data) {
        const flags = {};
        const evaluated = data?.evaluate?.flags ?? [];
        for (const entry of evaluated) {
            flags[entry.flagKey] = this.parseAwsJson(entry.value);
        }
        return flags;
    }
    /** evaluate.flags[].value is AWSJSON encoding a bare scalar (boolean | string | object). */
    parseAwsJson(raw) {
        return decodeEvaluatedFlagValue(raw);
    }
    /** onFlagChange.value is AWSJSON encoding `{ value, matchedRule }` (api-contract §4.2). */
    parsePushValue(raw) {
        try {
            const parsed = JSON.parse(raw);
            return {
                value: decodeEvaluatedFlagValue(parsed?.value ?? null),
                matchedRule: parsed?.matchedRule ?? null
            };
        }
        catch {
            this.logger.error('Could not parse onFlagChange AWSJSON value; treating flag as false.');
            return { value: false, matchedRule: null };
        }
    }
    buildUserFromInfo(info) {
        const key = info?.email ?? info?.sub;
        if (!key) {
            return null;
        }
        return {
            key,
            email: info?.email ?? key,
            name: info?.name ?? null
        };
    }
    /**
     * Lazily build a dedicated, hydrated AWSAppSyncClient for the feature-flag runtime endpoint.
     * The AWS_LAMBDA token is a function so every request/refresh pulls the current Okta bearer
     * token — no need to recreate the client on token renewal.
     */
    getClient() {
        if (!this.hydratedClient) {
            const config = this.requireConfig();
            const client = new AWSAppSyncClient({
                disableOffline: true,
                url: `${config.graphqlUrl}/graphql`,
                region: AWS_REGION,
                auth: {
                    type: AUTH_TYPE.AWS_LAMBDA,
                    token: () => this.getBearerToken()
                }
            });
            this.hydratedClient = client.hydrated();
        }
        return this.hydratedClient;
    }
    /** Current Okta access token as a Bearer string; empty string if unavailable. */
    async getBearerToken() {
        if (!this.oktaAuth) {
            return '';
        }
        try {
            const token = (await this.oktaAuth.tokenManager.get(OKTA_ACCESS_ENTRY_NAME));
            const accessToken = token?.accessToken ?? this.oktaAuth.getAccessToken();
            return accessToken ? `Bearer ${accessToken}` : '';
        }
        catch {
            const accessToken = this.oktaAuth.getAccessToken();
            return accessToken ? `Bearer ${accessToken}` : '';
        }
    }
    teardownSubscription() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        if (this.liveSubscription) {
            this.liveSubscription.unsubscribe();
            this.liveSubscription = null;
        }
    }
    requireConfig() {
        if (!this.config) {
            throw new Error('InHouseFeatureFlagClient used before start() supplied its runtime config.');
        }
        return this.config;
    }
    static { this.ɵfac = function InHouseFeatureFlagClient_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || InHouseFeatureFlagClient)(i0.ɵɵinject(i1.Store), i0.ɵɵinject(i2.LoggerService), i0.ɵɵinject(OKTA_AUTH, 8)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: InHouseFeatureFlagClient, factory: InHouseFeatureFlagClient.ɵfac, providedIn: 'root' }); }
}
//# sourceMappingURL=in-house-feature-flag-client.js.map